import { useState, useEffect } from "react";
import API, { formatWIB } from "../lib/api";
import { ClockClockwise } from "@phosphor-icons/react";

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    try {
      const params = filter ? `?entity_type=${filter}&limit=100` : "?limit=100";
      const { data } = await API.get(`/audit/logs${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const ENTITY_TYPES = ["", "user", "job", "job_record", "panel_source", "sheet_source", "field_mapping", "matching_rule"];

  return (
    <div data-testid="audit-log-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} entries</p>
        </div>
        <select
          data-testid="audit-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-zinc-300 rounded-sm px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          {ENTITY_TYPES.filter(Boolean).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div className="border border-zinc-200 rounded-sm">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0">
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Timestamp</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Action</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Entity</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Entity ID</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">User</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-400">No audit logs</td></tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-2 text-xs font-mono text-zinc-500 whitespace-nowrap">
                      {log.created_at ? formatWIB(log.created_at) : "-"}
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">{log.action}</span>
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-zinc-600">{log.entity_type}</td>
                    <td className="px-4 py-2 text-xs font-mono text-zinc-400">{log.entity_id?.slice(0, 8) || "-"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-600">{log.user_email || "-"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-500 min-w-[300px]">
                      {log.details ? (
                        <div className="space-y-0.5">
                          {Object.entries(log.details).map(([k, v]) => (
                            <div key={k} className="flex gap-1.5">
                              <span className="text-zinc-400 font-mono shrink-0">{k}:</span>
                              <span className="text-zinc-700 font-mono break-all">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      ) : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
